// Matched-coach model for the 1:1 discovery offer.
//
// ONE source of truth for "which coach did we match this person with", used
// by the checkout add-on gate, the dashboard upsell, AND the booking page —
// so the promise ("we matched you with Elisha") is kept on the calendar
// they actually land on.
//
// Routing (mirrors pricing tiers in create-checkout / coachingTier):
//   salary > $150k  → Linda Lutz, 60-min executive discovery ($67)
//   otherwise       → Elisha Austin or Linda Leake, 30-min ($49), split 50/50
//                     by a deterministic hash of the person's email (stable
//                     across devices, refreshes, and surfaces).

export interface MatchedCoach {
  key: 'elisha' | 'leake' | 'linda';
  name: string;
  firstName: string;
  photo: string | null;
  initials: string;
  rating: number | null;
  title: string;
  calLink: string;
  calNamespace: string;
  amountCents: 4900 | 6700;
  durationLabel: string;
  flavor: 'recruiter' | 'coach';
  exec: boolean;
  seeker: boolean;
  headline: string;
  subhead: string;
  whyLines: string[];
}

const PROFILES = {
  elisha: {
    key: 'elisha' as const, name: 'Elisha Austin', firstName: 'Elisha', initials: 'EA',
    photo: '/coaches/elisha-austin.webp', rating: null, title: 'Career coach',
    calLink: 'realign-coach-elisha/discovery-30', calNamespace: 'discovery-30',
  },
  leake: {
    key: 'leake' as const, name: 'Linda Leake', firstName: 'Linda', initials: 'LL',
    photo: '/coaches/linda-leake.webp', rating: null, title: 'Career coach',
    calLink: 'realign-coach-leake/discovery-30', calNamespace: 'discovery-30',
  },
  linda: {
    key: 'linda' as const, name: 'Linda Lutz', firstName: 'Linda', initials: 'LL',
    photo: '/coaches/linda-lutz.webp', rating: 4.95, title: 'Executive career coach',
    calLink: 'realign-coach-linda/discovery-60', calNamespace: 'discovery-60',
  },
};

const SEEKER_PURPOSES = new Set(['careerSwitch', 'return', 'lost']);

function bucket(key: string, n: number): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h % n;
}

export function matchCoach(
  variables: Record<string, string>,
  /** Stable identity for the 50/50 split — email preferred (cross-device). */
  identity: string | null | undefined,
  opts: { topCareer?: string | null; forceExec?: boolean } = {},
): MatchedCoach {
  const status = variables.employmentStatus ?? '';
  const purpose = variables.testTakingPurpose ?? '';
  const seeker = status === 'searching' || SEEKER_PURPOSES.has(purpose);
  const salary = parseInt((variables.currentSalary ?? '').replace(/[^0-9]/g, ''), 10);
  const exec = opts.forceExec ?? (Number.isFinite(salary) && salary > 150_000);

  const profile = exec
    ? PROFILES.linda
    : (bucket((identity ?? 'anon').trim().toLowerCase(), 2) === 0 ? PROFILES.elisha : PROFILES.leake);
  const first = profile.firstName;

  const why: string[] = [];
  if (status === 'searching') {
    why.push(`You said you're actively looking — ${first} works with clients on landing the next role, not just naming it.`);
  } else if (purpose === 'careerSwitch') {
    why.push(`You're weighing a career change — ${first} specializes in translating your experience into a new field.`);
  } else if (purpose === 'return') {
    why.push(`You're returning to work — ${first} helps rebuild the story and re-enter with confidence.`);
  } else if (purpose === 'lost') {
    why.push(`You said the next step isn't clear yet — ${first} turns "not sure" into a short list and a sequence.`);
  } else {
    why.push(`You're aiming higher where you are — ${first} coaches the promotion and raise path, step by step.`);
  }
  if (exec) {
    why.push(`At your level it's a 60-minute executive session: leadership transitions, positioning, and compensation.`);
  }
  if (opts.topCareer) {
    why.push(`Your #1 match is ${opts.topCareer} — you'll map the first 90 days toward it together.`);
  } else if (!exec) {
    why.push(`You'll leave with a written 90-day plan built around your top career matches.`);
  }

  return {
    ...profile,
    amountCents: exec ? 6700 : 4900,
    durationLabel: exec ? '60 minutes' : '30 minutes',
    flavor: seeker ? 'recruiter' : 'coach',
    exec,
    seeker,
    headline: `Based on your answers, we've matched you with ${profile.name}`,
    subhead: 'Book a 1:1 discovery session and leave with a 90-day career plan built around your report.',
    whyLines: why.slice(0, 3),
  };
}
